import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { getAuthToken } from '../../userLocalDetails';
import {
  MAINTENANCE_EVENT,
  getSubmissionMaintenance,
  updateSubmissionMaintenance,
  maintenanceAnswerBackup,
} from '../../utils/submissionMaintenance';

const Notice = styled.aside`
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: min(680px, calc(100% - 32px));
  box-sizing: border-box;
  padding: 14px 18px;
  border: 1px solid #bb8d35;
  border-radius: 10px;
  background: #fff8e7;
  color: #473413;
  box-shadow: 0 4px 18px #0002;
  z-index: 6000;
  font-size: 14px;
  line-height: 1.45;
  p {
    margin: 6px 0;
  }
  button {
    margin: 6px 12px 0 0;
    cursor: pointer;
  }
  ${({ $minimised }) =>
    $minimised &&
    `
    bottom: auto;
    top: 16px;
    right: 16px;
    left: auto;
    transform: none;
    width: auto;
    max-width: calc(100% - 32px);
  `}
`;

export default function SubmissionMaintenanceNotice() {
  const [status, setStatus] = useState(getSubmissionMaintenance);
  const [minimised, setMinimised] = useState(false);
  useEffect(() => setMinimised(false), [status.enabled]);
  useEffect(() => {
    const update = () => setStatus(getSubmissionMaintenance());
    window.addEventListener(MAINTENANCE_EVENT, update);
    const beforeUnload = (event) => {
      if (!getSubmissionMaintenance().unsavedAnswers) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener(MAINTENANCE_EVENT, update);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, []);
  // Poll only after a save was rejected for maintenance, until the server says it ended.
  // Polling every open tab (hidden and logged-out included) made this most of prod's traffic.
  useEffect(() => {
    if (!status.enabled) return undefined;
    let active = true;
    const controller = new AbortController();
    const refresh = async () => {
      try {
        const revision = getSubmissionMaintenance().revision;
        const token = getAuthToken();
        const base = (
          process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8080'
        ).replace(/\/+$/, '');
        const response = await fetch(`${base}/submissions/maintenance-status`, {
          credentials: 'include',
          cache: 'no-store',
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) return; // An old server or lost network does not mean maintenance ended.
        const next = await response.json();
        if (active && typeof next.enabled === 'boolean')
          updateSubmissionMaintenance(next, revision);
      } catch {
        /* Keep the last known state and the user's editor. */
      }
    };
    const timer = setInterval(refresh, 60000);
    return () => {
      active = false;
      controller.abort();
      clearInterval(timer);
    };
  }, [status.enabled]);

  if (!status.enabled && !status.unsavedAnswers) return null;
  const download = () => {
    const blob = new Blob(
      [JSON.stringify(maintenanceAnswerBackup(), null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'jeddle-unsaved-responses.json';
    anchor.click();
    URL.revokeObjectURL(url);
  };
  return createPortal(
    <Notice
      $minimised={minimised}
      role="status"
      aria-live="polite"
      aria-label="Jeddle maintenance"
    >
      <strong>
        {status.enabled
          ? 'A short pause for an update'
          : 'Jeddle is available again'}
      </strong>
      <button
        type="button"
        onClick={() => setMinimised(!minimised)}
        aria-expanded={!minimised}
      >
        {minimised ? 'Show save guidance' : 'Minimise notice'}
      </button>
      {!minimised && (
        <p>
          {status.enabled
            ? status.message
            : 'Your response changes are still unsaved. Return to the editor and save or submit again before closing this tab.'}
        </p>
      )}
      {!minimised && !!status.unsavedAnswers && (
        <>
          <p>
            Copies from failed saves are held in this tab only. Copy any newer
            writing from the editor too.
          </p>
          <button type="button" onClick={download}>
            Download unsaved responses
          </button>
        </>
      )}
    </Notice>,
    document.body
  );
}
